import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/hooks/useLanguage';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Video, Phone, MapPin, Loader2, CheckCircle, Clock, CalendarDays } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface AppointmentStepProps {
  form: UseFormReturn<any>;
  onSelect?: () => void;
}

interface TimeSlot {
  time: string;
  displayTime: string;
  available: boolean;
}

const AppointmentStep = ({ form, onSelect }: AppointmentStepProps) => {
  const { t, language } = useLanguage();
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  const preferredDate = form.watch('preferredDate');
  const preferredTime = form.watch('preferredTime');

  // Fetch available slots when date changes
  const fetchAvailableSlots = useCallback(async (date: string) => {
    if (!date) {
      setAvailableSlots([]);
      return;
    }

    setIsLoadingSlots(true);
    setSlotsError(null);

    try {
      const { data, error } = await supabase.functions.invoke('get-available-slots', {
        body: {
          date,
          timezone: 'America/Chicago',
        },
      });

      if (error) throw error;

      setAvailableSlots(data?.slots || []);
      setCalendarConnected(data?.calendarConnected || false);
      
      // Clear time if it's no longer available
      if (preferredTime) {
        const selectedSlot = data?.slots?.find((s: TimeSlot) => s.time === preferredTime);
        if (!selectedSlot || !selectedSlot.available) {
          form.setValue('preferredTime', '');
        }
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setSlotsError(
        language === 'es'
          ? 'No se pudieron cargar los horarios. Por favor intente de nuevo.'
          : 'Could not load available times. Please try again.'
      );
      setAvailableSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  }, [form, language, preferredTime]);

  useEffect(() => {
    if (preferredDate) {
      fetchAvailableSlots(preferredDate);
    }
  }, [preferredDate, fetchAvailableSlots]);

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get maximum date (3 months from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  const handleTimeSelect = (time: string) => {
    form.setValue('preferredTime', time);
    form.clearErrors('preferredTime');
    onSelect?.();
  };

  const availableSlotsCount = availableSlots.filter(s => s.available).length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2">{t('booking.appointmentType')}</h3>
        <p className="text-muted-foreground">{t('booking.chooseDateTime')}</p>
      </div>

      <FormField
        control={form.control}
        name="appointmentType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('booking.appointmentType')} *</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="In-person" id="in-person" />
                      <label 
                        htmlFor="in-person" 
                        className="flex items-center gap-2 cursor-pointer font-medium"
                      >
                        <MapPin className="h-4 w-4" />
                        {t('booking.inPerson')}
                      </label>
                    </div>
                  </FormControl>
                </FormItem>
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Phone" id="phone" />
                      <label 
                        htmlFor="phone" 
                        className="flex items-center gap-2 cursor-pointer font-medium"
                      >
                        <Phone className="h-4 w-4" />
                        {t('booking.phoneCall')}
                      </label>
                    </div>
                  </FormControl>
                </FormItem>
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Video" id="video" />
                      <label 
                        htmlFor="video" 
                        className="flex items-center gap-2 cursor-pointer font-medium"
                      >
                        <Video className="h-4 w-4" />
                        {t('booking.video')}
                      </label>
                    </div>
                  </FormControl>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Date Selection */}
      <FormField
        control={form.control}
        name="preferredDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              {language === 'es' ? 'Seleccione una fecha' : 'Select a date'} *
            </FormLabel>
            <FormControl>
              <Input 
                {...field} 
                type="date" 
                min={getMinDate()}
                max={getMaxDate()}
                required 
                className="max-w-xs"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Time Slots */}
      {preferredDate && (
        <FormField
          control={form.control}
          name="preferredTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {language === 'es' ? 'Seleccione un horario' : 'Select a time'} *
              </FormLabel>
              
              {isLoadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">
                    {language === 'es' ? 'Cargando horarios disponibles...' : 'Loading available times...'}
                  </span>
                </div>
              ) : slotsError ? (
                <Alert variant="destructive">
                  <AlertDescription>{slotsError}</AlertDescription>
                </Alert>
              ) : availableSlots.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    {language === 'es' 
                      ? 'No hay horarios disponibles para esta fecha. Por favor seleccione otra fecha.'
                      : 'No available times for this date. Please select another date.'}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* Available slots count */}
                  <p className="text-sm text-muted-foreground mb-3">
                    {language === 'es' 
                      ? `${availableSlotsCount} horarios disponibles`
                      : `${availableSlotsCount} times available`}
                    {calendarConnected && (
                      <span className="ml-2 text-green-600 dark:text-green-400">
                        <CheckCircle className="h-3 w-3 inline mr-1" />
                        {language === 'es' ? 'Calendario sincronizado' : 'Calendar synced'}
                      </span>
                    )}
                  </p>

                  {/* Time slots grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {availableSlots.map((slot) => (
                      <Button
                        key={slot.time}
                        type="button"
                        variant={field.value === slot.time ? "default" : "outline"}
                        size="sm"
                        disabled={!slot.available}
                        onClick={() => slot.available && handleTimeSelect(slot.time)}
                        className={cn(
                          "h-10 text-sm font-medium transition-all",
                          !slot.available && "opacity-40 cursor-not-allowed line-through bg-muted",
                          field.value === slot.time && "ring-2 ring-primary ring-offset-2",
                          slot.available && field.value !== slot.time && "hover:bg-primary/10 hover:border-primary"
                        )}
                      >
                        {slot.displayTime}
                      </Button>
                    ))}
                  </div>

                  {/* Timezone note */}
                  <p className="text-xs text-muted-foreground mt-3">
                    {language === 'es' 
                      ? 'Todos los horarios están en Hora Central (Houston, TX)'
                      : 'All times are in Central Time (Houston, TX)'}
                  </p>
                </>
              )}
              
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Selected time confirmation */}
      {preferredDate && preferredTime && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-300">
            {language === 'es' 
              ? `Cita seleccionada: ${new Date(preferredDate).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} a las ${availableSlots.find(s => s.time === preferredTime)?.displayTime || preferredTime}`
              : `Selected: ${new Date(preferredDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${availableSlots.find(s => s.time === preferredTime)?.displayTime || preferredTime}`}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default AppointmentStep;
